import { useState } from 'react';
import {
  Box,
  Typography,
  Stack,
  TextField,
  Button,
  CircularProgress,
  Chip,
} from '@mui/material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import { useApi } from '@/providers/ApiContext';
import { updatePlotElement } from '@/actions/plotElements';
import PLOT_STATUSES from '@/components/plotStatuses';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';
import type { PlotElement as PlotElementType, EntityId } from '@/types';

interface PlotElementProps {
  element: PlotElementType;
  plotId: string | undefined;
  sectionId: EntityId;
  plotStatus?: string;
  onElementUpdated: () => void;
}

function PlotElement({
  element,
  plotId,
  sectionId,
  plotStatus,
  onElementUpdated,
}: PlotElementProps): React.JSX.Element {
  const api = useApi();
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [editedContent, setEditedContent] = useState<string>(element.content || '');
  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [showPreview, setShowPreview] = useState<boolean>(false);

  // Check if this element should be editable
  const isEditable =
    plotStatus === PLOT_STATUSES.PENDING &&
    (element.status === PLOT_STATUSES.COMPLETE || element.status === 'errored');

  // Check if we should show the original display format (for draft plots)
  const showOriginalFormat = plotStatus === PLOT_STATUSES.DRAFT;

  // Check if we should show minimal status display (for running plots with queued/running elements)
  const showMinimalStatus =
    plotStatus === PLOT_STATUSES.RUNNING &&
    (element.status === 'queued' || element.status === 'running');

  const getElementTypeColor = (type: string): 'primary' | 'secondary' | 'success' | 'warning' | 'default' => {
    switch (type) {
      case 'narrative':
        return 'primary';
      case 'image':
        return 'secondary';
      case 'table':
        return 'success';
      case 'chart':
        return 'warning';
      default:
        return 'default';
    }
  };

  const getElementTypeLabel = (type: string): string => {
    switch (type) {
      case 'narrative':
        return 'Narrative';
      case 'image':
        return 'Image';
      case 'table':
        return 'Table';
      case 'chart':
        return 'Chart';
      default:
        return type || 'Unknown';
    }
  };

  const getStatusColor = (status?: string): 'success' | 'warning' | 'info' | 'error' | 'default' => {
    switch (status) {
      case 'ready':
        return 'success';
      case 'draft':
        return 'warning';
      case 'processing':
        return 'info';
      case 'running':
        return 'info';
      case 'queued':
        return 'default';
      case 'errored':
        return 'error';
      default:
        return 'default';
    }
  };

  const handleEdit = () => {
    setIsEditing(true);
    setEditedContent(element.content || '');
    setError(null);
    setShowPreview(false);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditedContent(element.content || '');
    setError(null);
  };

  const handleSave = async () => {
    if (!editedContent.trim()) {
      setError('Content cannot be empty');
      return;
    }

    try {
      setSaving(true);
      setError(null);

      if (!plotId || !api) return;
      await updatePlotElement(api, plotId as EntityId, sectionId, element.entity_id, {
        content: editedContent.trim(),
      });

      setIsEditing(false);
      // Trigger refresh of the parent component to get updated content
      if (onElementUpdated) {
        onElementUpdated();
      }
    } catch (err) {
      console.error('Error updating plot element:', err);
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to save changes';
      setError(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ mb: 3 }}>
      {/* Show minimal status for running plots with queued/running elements */}
      {showMinimalStatus ? (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            p: 2,
            bgcolor: 'grey.50',
            borderRadius: 1,
            border: '1px solid',
            borderColor: 'grey.200',
          }}
        >
          <Stack direction='row' spacing={1}>
            <Chip
              label={getElementTypeLabel(element.type)}
              color={getElementTypeColor(element.type)}
              size='small'
              variant='outlined'
            />
            <Chip
              label={element.status || 'unknown'}
              color={getStatusColor(element.status)}
              size='small'
              variant='outlined'
            />
          </Stack>
          {element.status === 'running' && (
            <CircularProgress size={16} sx={{ ml: 1 }} />
          )}
        </Box>
      ) : showOriginalFormat ? (
        <>
          {/* Element content as paragraph */}
          {element.content && (
            <Typography variant='body1' sx={{ mb: 2, lineHeight: 1.6 }}>
              {element.content}
            </Typography>
          )}

          {/* Show chart_element description for draft elements */}
          {element.status === 'draft' && element.chart_element?.description && (
            <Box
              sx={{
                mb: 2,
                p: 2,
                bgcolor: 'grey.100',
                borderRadius: 1,
                border: '1px dashed',
                borderColor: 'grey.300',
              }}
            >
              <Typography
                variant='body2'
                sx={{ fontStyle: 'italic', color: 'text.secondary' }}
              >
                <strong>Description:</strong>{' '}
                {element.chart_element.description}
              </Typography>
              {element.chart_element.prompt &&
                element.chart_element.prompt !==
                  element.chart_element.description && (
                  <Typography
                    variant='body2'
                    sx={{ fontStyle: 'italic', color: 'text.secondary', mt: 1 }}
                  >
                    <strong>Prompt:</strong> {element.chart_element.prompt}
                  </Typography>
                )}
              {element.chart_element.metadata?.title && (
                <Typography
                  variant='body2'
                  sx={{ fontStyle: 'italic', color: 'text.secondary', mt: 1 }}
                >
                  <strong>Title:</strong> {element.chart_element.metadata.title}
                </Typography>
              )}
              {element.chart_element.metadata?.headers &&
                element.chart_element.metadata.headers.length > 0 && (
                  <Typography
                    variant='body2'
                    sx={{ fontStyle: 'italic', color: 'text.secondary', mt: 1 }}
                  >
                    <strong>Headers:</strong>{' '}
                    {element.chart_element.metadata.headers.join(', ')}
                  </Typography>
                )}
            </Box>
          )}

          {/* Element metadata in a subtle way */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              mb: 1,
              opacity: 0.7,
            }}
          >
            <Stack direction='row' spacing={1}>
              <Chip
                label={getElementTypeLabel(element.type)}
                color={getElementTypeColor(element.type)}
                size='small'
                variant='outlined'
              />
              <Chip
                label={element.status || 'unknown'}
                color={getStatusColor(element.status)}
                size='small'
                variant='outlined'
              />
            </Stack>
          </Box>

          {/* Waypoints as references */}
          {Array.isArray(element.waypoints) &&
            element.waypoints.length > 0 &&
            typeof element.waypoints[0] === 'object' &&
            'name' in element.waypoints[0] && (
              <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Typography
                  variant='body2'
                  sx={{ mb: 1, fontWeight: 'bold', fontSize: '0.875rem' }}
                >
                  References:
                </Typography>
                {(element.waypoints as Array<{
                  entity_id: EntityId;
                  name: string;
                  markers?: Array<{ entity_id: EntityId; name: string }>;
                }>).map((waypoint, index) => (
                  <Box key={waypoint.entity_id || index} sx={{ ml: 1, mb: 0.5 }}>
                    <Typography
                      variant='body2'
                      color='text.secondary'
                      sx={{ fontSize: '0.8rem' }}
                    >
                      • {waypoint.name}
                      {waypoint.markers && waypoint.markers.length > 0 && (
                        <span> ({waypoint.markers.length} markers)</span>
                      )}
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}
        </>
      ) : (
        <>
          {/* Element content as paragraph or editable field */}
          {isEditing ? (
            <Box sx={{ mb: 2 }}>
              <Stack direction='row' spacing={1} sx={{ mb: 1 }}>
                <Button
                  variant={!showPreview ? 'contained' : 'outlined'}
                  onClick={() => setShowPreview(false)}
                  size='small'
                >
                  Edit
                </Button>
                <Button
                  variant={showPreview ? 'contained' : 'outlined'}
                  onClick={() => setShowPreview(true)}
                  size='small'
                >
                  Preview
                </Button>
              </Stack>

              {!showPreview ? (
                <TextField
                  fullWidth
                  multiline
                  rows={8}
                  value={editedContent}
                  onChange={(e) => setEditedContent(e.target.value)}
                  variant='outlined'
                  placeholder='Enter markdown content... (supports tables, headers, lists, etc.)'
                  sx={{
                    mb: 1,
                    '& .MuiInputBase-input': {
                      fontFamily: 'monospace',
                      fontSize: '0.875rem',
                      lineHeight: 1.5,
                    },
                  }}
                />
              ) : (
                <Box
                  sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    p: 2,
                    mb: 1,
                    minHeight: '200px',
                    bgcolor: 'background.paper',
                  }}
                >
                  {editedContent ? (
                    <ReactMarkdown
                      remarkPlugins={[remarkGfm, remarkBreaks]}
                      components={{
                        p: ({ children }) => (
                          <Typography
                            variant='body1'
                            sx={{
                              lineHeight: 1.6,
                              mb: 1,
                            }}
                          >
                            {children}
                          </Typography>
                        ),
                        h1: ({ children }) => (
                          <Typography
                            variant='h4'
                            sx={{ mb: 2, mt: 2, fontWeight: 'bold' }}
                          >
                            {children}
                          </Typography>
                        ),
                        h2: ({ children }) => (
                          <Typography
                            variant='h5'
                            sx={{ mb: 1.5, mt: 1.5, fontWeight: 'bold' }}
                          >
                            {children}
                          </Typography>
                        ),
                        h3: ({ children }) => (
                          <Typography
                            variant='h6'
                            sx={{ mb: 1, mt: 1, fontWeight: 'bold' }}
                          >
                            {children}
                          </Typography>
                        ),
                        h4: ({ children }) => (
                          <Typography
                            variant='subtitle1'
                            sx={{ mb: 1, mt: 1, fontWeight: 'bold' }}
                          >
                            {children}
                          </Typography>
                        ),
                        h5: ({ children }) => (
                          <Typography
                            variant='subtitle2'
                            sx={{ mb: 0.5, mt: 0.5, fontWeight: 'bold' }}
                          >
                            {children}
                          </Typography>
                        ),
                        h6: ({ children }) => (
                          <Typography
                            variant='body2'
                            sx={{ mb: 0.5, mt: 0.5, fontWeight: 'bold' }}
                          >
                            {children}
                          </Typography>
                        ),
                        ul: ({ children }) => (
                          <Box component='ul' sx={{ pl: 2, mb: 1 }}>
                            {children}
                          </Box>
                        ),
                        ol: ({ children }) => (
                          <Box component='ol' sx={{ pl: 2, mb: 1 }}>
                            {children}
                          </Box>
                        ),
                        li: ({ children }) => (
                          <Box component='li' sx={{ mb: 0.5 }}>
                            {children}
                          </Box>
                        ),
                        table: ({ children }) => (
                          <Box
                            component='table'
                            sx={{
                              width: '100%',
                              borderCollapse: 'collapse',
                              mb: 2,
                              border: '1px solid',
                              borderColor: 'divider',
                            }}
                          >
                            {children}
                          </Box>
                        ),
                        th: ({ children }) => (
                          <Box
                            component='th'
                            sx={{
                              border: '1px solid',
                              borderColor: 'divider',
                              p: 1,
                              bgcolor: 'grey.100',
                              fontWeight: 'bold',
                              textAlign: 'left',
                            }}
                          >
                            {children}
                          </Box>
                        ),
                        td: ({ children }) => (
                          <Box
                            component='td'
                            sx={{
                              border: '1px solid',
                              borderColor: 'divider',
                              p: 1,
                            }}
                          >
                            {children}
                          </Box>
                        ),
                        code: ({ children }) => (
                          <Box
                            component='code'
                            sx={{
                              bgcolor: 'grey.100',
                              px: 0.5,
                              py: 0.25,
                              borderRadius: 0.5,
                              fontFamily: 'monospace',
                              fontSize: '0.875em',
                            }}
                          >
                            {children}
                          </Box>
                        ),
                        pre: ({ children }) => (
                          <Box
                            component='pre'
                            sx={{
                              bgcolor: 'grey.100',
                              p: 2,
                              borderRadius: 1,
                              overflow: 'auto',
                              mb: 1,
                              fontFamily: 'monospace',
                            }}
                          >
                            {children}
                          </Box>
                        ),
                        blockquote: ({ children }) => (
                          <Box
                            component='blockquote'
                            sx={{
                              borderLeft: '4px solid',
                              borderColor: 'primary.main',
                              pl: 2,
                              ml: 0,
                              mb: 1,
                              fontStyle: 'italic',
                            }}
                          >
                            {children}
                          </Box>
                        ),
                      }}
                    >
                      {editedContent}
                    </ReactMarkdown>
                  ) : (
                    <Typography
                      variant='body2'
                      color='text.secondary'
                      sx={{ fontStyle: 'italic' }}
                    >
                      No content to preview
                    </Typography>
                  )}
                </Box>
              )}

              {error && (
                <Typography variant='body2' color='error' sx={{ mb: 1 }}>
                  {error}
                </Typography>
              )}
              <Stack direction='row' spacing={1}>
                <Button
                  variant='contained'
                  startIcon={
                    saving ? (
                      <CircularProgress size={16} />
                    ) : (
                      <SaveOutlinedIcon />
                    )
                  }
                  onClick={handleSave}
                  disabled={saving || !editedContent.trim()}
                  size='small'
                >
                  {saving ? 'Saving...' : 'Save'}
                </Button>
                <Button
                  variant='outlined'
                  startIcon={<CancelOutlinedIcon />}
                  onClick={handleCancel}
                  disabled={saving}
                  size='small'
                >
                  Cancel
                </Button>
              </Stack>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', gap: 3, mb: 2 }}>
              {/* Main content area */}
              <Box sx={{ flex: 1 }}>
                {element.content ? (
                  element.type === 'title' ? (
                    <Typography
                      variant='h3'
                      sx={{
                        mb: 3,
                        mt: 2,
                        fontWeight: 'bold',
                        borderBottom: '2px solid',
                        pb: 1,
                      }}
                    >
                      {element.content}
                    </Typography>
                  ) : (
                    <ReactMarkdown
                      remarkPlugins={[remarkGfm, remarkBreaks]}
                      components={{
                        // Custom renderers to match MUI Typography styles
                        p: ({ children }) => (
                          <Typography
                            variant='body1'
                            sx={{
                              lineHeight: 1.6,
                              mb: 1,
                            }}
                          >
                            {children}
                          </Typography>
                        ),
                        h1: ({ children }) => (
                          <Typography
                            variant='h4'
                            sx={{ mb: 2, mt: 2, fontWeight: 'bold' }}
                          >
                            {children}
                          </Typography>
                        ),
                        h2: ({ children }) => (
                          <Typography
                            variant='h5'
                            sx={{ mb: 1.5, mt: 1.5, fontWeight: 'bold' }}
                          >
                            {children}
                          </Typography>
                        ),
                        h3: ({ children }) => (
                          <Typography
                            variant='h6'
                            sx={{ mb: 1, mt: 1, fontWeight: 'bold' }}
                          >
                            {children}
                          </Typography>
                        ),
                        h4: ({ children }) => (
                          <Typography
                            variant='subtitle1'
                            sx={{ mb: 1, mt: 1, fontWeight: 'bold' }}
                          >
                            {children}
                          </Typography>
                        ),
                        h5: ({ children }) => (
                          <Typography
                            variant='subtitle2'
                            sx={{ mb: 0.5, mt: 0.5, fontWeight: 'bold' }}
                          >
                            {children}
                          </Typography>
                        ),
                        h6: ({ children }) => (
                          <Typography
                            variant='body2'
                            sx={{ mb: 0.5, mt: 0.5, fontWeight: 'bold' }}
                          >
                            {children}
                          </Typography>
                        ),
                        ul: ({ children }) => (
                          <Box component='ul' sx={{ pl: 2, mb: 1 }}>
                            {children}
                          </Box>
                        ),
                        ol: ({ children }) => (
                          <Box component='ol' sx={{ pl: 2, mb: 1 }}>
                            {children}
                          </Box>
                        ),
                        li: ({ children }) => (
                          <Box component='li' sx={{ mb: 0.5 }}>
                            {children}
                          </Box>
                        ),
                        table: ({ children }) => (
                          <Box
                            component='table'
                            sx={{
                              width: '100%',
                              borderCollapse: 'collapse',
                              mb: 2,
                              border: '1px solid',
                              borderColor: 'divider',
                            }}
                          >
                            {children}
                          </Box>
                        ),
                        th: ({ children }) => (
                          <Box
                            component='th'
                            sx={{
                              border: '1px solid',
                              borderColor: 'divider',
                              p: 1,
                              bgcolor: 'grey.100',
                              fontWeight: 'bold',
                              textAlign: 'left',
                            }}
                          >
                            {children}
                          </Box>
                        ),
                        td: ({ children }) => (
                          <Box
                            component='td'
                            sx={{
                              border: '1px solid',
                              borderColor: 'divider',
                              p: 1,
                            }}
                          >
                            {children}
                          </Box>
                        ),
                        code: ({ children }) => (
                          <Box
                            component='code'
                            sx={{
                              bgcolor: 'grey.100',
                              px: 0.5,
                              py: 0.25,
                              borderRadius: 0.5,
                              fontFamily: 'monospace',
                              fontSize: '0.875em',
                            }}
                          >
                            {children}
                          </Box>
                        ),
                        pre: ({ children }) => (
                          <Box
                            component='pre'
                            sx={{
                              bgcolor: 'grey.100',
                              p: 2,
                              borderRadius: 1,
                              overflow: 'auto',
                              mb: 1,
                              fontFamily: 'monospace',
                            }}
                          >
                            {children}
                          </Box>
                        ),
                        blockquote: ({ children }) => (
                          <Box
                            component='blockquote'
                            sx={{
                              borderLeft: '4px solid',
                              borderColor: 'primary.main',
                              pl: 2,
                              ml: 0,
                              mb: 1,
                              fontStyle: 'italic',
                            }}
                          >
                            {children}
                          </Box>
                        ),
                      }}
                    >
                      {element.content}
                    </ReactMarkdown>
                  )
                ) : (
                  <Typography
                    variant='body1'
                    sx={{
                      lineHeight: 1.6,
                      color: 'text.secondary',
                      fontStyle: 'italic',
                    }}
                  >
                    {isEditable
                      ? 'Click edit to add content...'
                      : 'No content available'}
                  </Typography>
                )}
              </Box>

              {/* Right sidebar with controls and references */}
              <Box sx={{ minWidth: 200, maxWidth: 300 }}>
                <Stack spacing={2}>
                  {/* Edit button */}
                  {isEditable && (
                    <Box>
                      <Button
                        variant='text'
                        startIcon={<EditOutlinedIcon />}
                        onClick={handleEdit}
                        size='small'
                        sx={{
                          fontSize: '0.75rem',
                          minWidth: 'auto',
                          px: 1,
                          py: 0.5,
                        }}
                      >
                        Edit
                      </Button>
                    </Box>
                  )}

                  {/* References */}
                  {Array.isArray(element.waypoints) &&
                    element.waypoints.length > 0 &&
                    typeof element.waypoints[0] === 'object' &&
                    'name' in element.waypoints[0] && (
                      <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                        <Typography
                          variant='body2'
                          sx={{ mb: 1, fontWeight: 'bold', fontSize: '0.875rem' }}
                        >
                          References:
                        </Typography>
                        {(element.waypoints as Array<{
                          entity_id: EntityId;
                          name: string;
                          markers?: Array<{ entity_id: EntityId; name: string }>;
                        }>).map((waypoint, index) => (
                          <Box key={waypoint.entity_id || index} sx={{ mb: 0.5 }}>
                            <Typography
                              variant='body2'
                              color='text.secondary'
                              sx={{ fontSize: '0.8rem' }}
                            >
                              • {waypoint.name}
                              {waypoint.markers &&
                                waypoint.markers.length > 0 && (
                                  <span>
                                    {' '}
                                    ({waypoint.markers.length} markers)
                                  </span>
                                )}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    )}
                </Stack>
              </Box>
            </Box>
          )}

          {/* Show chart_element description for draft elements */}
          {element.status === 'draft' && element.chart_element?.description && (
            <Box
              sx={{
                mb: 2,
                p: 2,
                bgcolor: 'grey.100',
                borderRadius: 1,
                border: '1px dashed',
                borderColor: 'grey.300',
              }}
            >
              <Typography
                variant='body2'
                sx={{ fontStyle: 'italic', color: 'text.secondary' }}
              >
                <strong>Description:</strong>{' '}
                {element.chart_element.description}
              </Typography>
              {element.chart_element.prompt &&
                element.chart_element.prompt !==
                  element.chart_element.description && (
                  <Typography
                    variant='body2'
                    sx={{ fontStyle: 'italic', color: 'text.secondary', mt: 1 }}
                  >
                    <strong>Prompt:</strong> {element.chart_element.prompt}
                  </Typography>
                )}
              {element.chart_element.metadata?.title && (
                <Typography
                  variant='body2'
                  sx={{ fontStyle: 'italic', color: 'text.secondary', mt: 1 }}
                >
                  <strong>Title:</strong> {element.chart_element.metadata.title}
                </Typography>
              )}
              {element.chart_element.metadata?.headers &&
                element.chart_element.metadata.headers.length > 0 && (
                  <Typography
                    variant='body2'
                    sx={{ fontStyle: 'italic', color: 'text.secondary', mt: 1 }}
                  >
                    <strong>Headers:</strong>{' '}
                    {element.chart_element.metadata.headers.join(', ')}
                  </Typography>
                )}
            </Box>
          )}
        </>
      )}
    </Box>
  );
}

export default PlotElement;

